// src/pages/Logistics/DriverLogistics.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy, addDoc, serverTimestamp, doc, setDoc, increment } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent',
        minHeight: '100vh'
    },
    // Mobile-first header
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginBottom: '24px'
    },
    headerTop: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
    },
    title: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    subtitle: {
        color: '#64748b',
        fontSize: '14px',
        marginTop: '4px'
    },
    headerActions: {
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap'
    },
    // Cards - Mobile friendly
    card: {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        padding: '20px',
        marginBottom: '20px'
    },
    cardTitle: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#0f172a',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    // Form elements - Large for mobile
    formGroup: {
        marginBottom: '16px'
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#374151',
        fontSize: '14px'
    },
    input: {
        width: '100%',
        padding: '14px 16px',
        fontSize: '16px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    select: {
        width: '100%',
        padding: '14px 16px',
        fontSize: '16px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        background: 'white',
        outline: 'none'
    },
    // Buttons - Touch friendly (min 44px)
    button: {
        padding: '14px 24px',
        border: 'none',
        background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
        color: 'white',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '15px',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
        transition: 'all 0.2s',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        minHeight: '48px'
    },
    buttonSecondary: {
        padding: '12px 20px',
        border: '1px solid #e2e8f0',
        background: 'white',
        color: '#374151',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        transition: 'all 0.2s',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        minHeight: '44px'
    },
    buttonSuccess: {
        background: 'linear-gradient(135deg, #10b981, #059669)',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
    },
    buttonDanger: {
        background: '#ef4444',
        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
    },
    // Type toggle buttons
    typeToggle: {
        display: 'flex',
        gap: '12px',
        marginBottom: '16px'
    },
    typeButton: {
        flex: 1,
        padding: '16px',
        border: '2px solid #e2e8f0',
        borderRadius: '12px',
        background: 'white',
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'all 0.2s',
        minHeight: '70px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px'
    },
    typeButtonActive: {
        borderColor: '#06C167',
        background: '#eef2ff',
        color: '#4f46e5'
    },
    typeButtonPickup: {
        borderColor: '#10b981',
        background: '#ecfdf5',
        color: '#059669'
    },
    typeButtonDelivery: {
        borderColor: '#f59e0b',
        background: '#fffbeb',
        color: '#d97706'
    },
    typeIcon: {
        fontSize: '24px'
    },
    typeLabel: {
        fontSize: '14px',
        fontWeight: '600'
    },
    // Items list
    itemsList: {
        listStyle: 'none',
        padding: 0,
        margin: 0
    },
    itemRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px',
        background: '#f8fafc',
        borderRadius: '10px',
        marginBottom: '10px'
    },
    itemInfo: {
        flex: 1
    },
    itemName: {
        fontWeight: '600',
        fontSize: '14px',
        color: '#0f172a'
    },
    itemQuantity: {
        fontSize: '13px',
        color: '#64748b',
        marginTop: '4px'
    },
    removeButton: {
        padding: '8px',
        border: 'none',
        background: '#fee2e2',
        color: '#ef4444',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '16px',
        minWidth: '36px',
        minHeight: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    // Table for history - Scrollable on mobile
    tableContainer: {
        overflowX: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: '12px'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        minWidth: '600px'
    },
    th: {
        background: '#f8fafc',
        padding: '14px 16px',
        textAlign: 'left',
        borderBottom: '2px solid #e2e8f0',
        color: '#64748b',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        whiteSpace: 'nowrap'
    },
    td: {
        padding: '14px 16px',
        borderBottom: '1px solid #f1f5f9',
        color: '#334155',
        fontSize: '14px'
    },
    // Badges
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600'
    },
    badgePickup: {
        background: '#dcfce7',
        color: '#06C167'
    },
    badgeDelivery: {
        background: '#fef3c7',
        color: '#d97706'
    },
    // Filters
    filterContainer: {
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        marginBottom: '16px'
    },
    filterInput: {
        padding: '12px 14px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        fontSize: '14px',
        outline: 'none',
        minWidth: '140px',
        flex: '1 1 140px'
    },
    filterSelect: {
        padding: '12px 14px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        fontSize: '14px',
        background: 'white',
        outline: 'none',
        minWidth: '130px',
        flex: '1 1 130px'
    },
    // Empty state
    emptyState: {
        textAlign: 'center',
        padding: '48px 24px',
        color: '#64748b'
    },
    emptyIcon: {
        fontSize: '48px',
        marginBottom: '16px'
    },
    // Grid for form
    formGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px'
    },
    // Sticky submit on mobile
    stickySubmit: {
        position: 'sticky',
        bottom: '20px',
        background: 'white',
        padding: '16px',
        borderRadius: '16px',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginTop: '20px'
    },
    // Search Results Dropdown
    searchResults: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        maxHeight: '200px',
        overflowY: 'auto',
        zIndex: 10,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        marginTop: '4px'
    },
    searchResultItem: {
        padding: '12px 16px',
        borderBottom: '1px solid #f1f5f9',
        cursor: 'pointer',
        fontSize: '14px',
        color: '#0f172a',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    searchResultItem: {
        padding: '12px 16px',
        borderBottom: '1px solid #f1f5f9',
        cursor: 'pointer',
        fontSize: '14px',
        color: '#0f172a',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    }
};

function ProductSearchModal({ isOpen, onClose, onSelect, products }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);

    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
            setFilteredProducts([]);
            return;
        }

        // Focus input when modal opens (optional, might need ref)
    }, [isOpen]);

    useEffect(() => {
        if (!searchTerm) {
            setFilteredProducts([]);
            return;
        }

        const term = searchTerm.toLowerCase();
        const results = products.filter(product => {
            const nameMatch = product.name?.toLowerCase().includes(term);
            const barcodeMatch = product.barcode?.toLowerCase() === term || product.barcode?.toLowerCase().includes(term);
            return nameMatch || barcodeMatch;
        });

        setFilteredProducts(results);

        // Exact barcode match auto-select
        const exactMatch = products.find(p => p.barcode?.toLowerCase() === term);
        if (exactMatch && results.length === 1) {
            onSelect(exactMatch);
        }
    }, [searchTerm, products, onSelect]);

    if (!isOpen) return null;

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={{ ...styles.modalContent, height: '80vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>📦 Ürün Ara</h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#94a3b8'
                        }}
                    >
                        ×
                    </button>
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <input
                        type="text"
                        style={styles.input}
                        placeholder="Barkod veya ürün adı..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                    />
                </div>

                <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                    {searchTerm ? (
                        filteredProducts.length > 0 ? (
                            filteredProducts.map(product => (
                                <div
                                    key={product.id}
                                    style={styles.searchResultItem}
                                    onClick={() => onSelect(product)}
                                >
                                    <span style={{ fontWeight: '500' }}>{product.name}</span>
                                    {product.barcode && (
                                        <span style={{ fontSize: '12px', color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                                            {product.barcode}
                                        </span>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                                Ürün bulunamadı
                            </div>
                        )
                    ) : (
                        <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                            Aramak için yazmaya başlayın...
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function DriverLogistics() {
    const { userData, currentUser } = useAuth();
    const [drivers, setDrivers] = useState([]);

    const [subcontractors, setSubcontractors] = useState([]);
    const [suppliers, setSuppliers] = useState([]); // New state for suppliers
    const [products, setProducts] = useState([]);
    const [transfers, setTransfers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Form state
    const [selectedDriverId, setSelectedDriverId] = useState('');
    const [selectedLocationId, setSelectedLocationId] = useState(''); // Changed from selectedSubcontractorId
    const [transferType, setTransferType] = useState('pickup'); // pickup or delivery
    const [items, setItems] = useState([]);
    const [currentProduct, setCurrentProduct] = useState({ productId: '', productName: '', quantity: '', unit: 'Adet' });
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [filteredProducts, setFilteredProducts] = useState([]);

    // Filter state
    const [filterDriver, setFilterDriver] = useState('all');
    const [filterLocation, setFilterLocation] = useState('all'); // Changed from filterSubcontractor
    const [filterType, setFilterType] = useState('all');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');

    // Add driver modal
    const [showAddDriver, setShowAddDriver] = useState(false);
    const [newDriverName, setNewDriverName] = useState('');
    const [newDriverPhone, setNewDriverPhone] = useState('');

    // Identify logged-in driver
    const loggedInDriver = useMemo(() => {
        if (!currentUser || drivers.length === 0) return null;

        const userEmail = currentUser.email?.toLowerCase();
        const userName = currentUser.displayName?.toLowerCase();

        // Debugging logs to help troubleshoot
        console.log('Detecting driver for:', { email: userEmail, phone: currentUser.phoneNumber, name: userName });

        // Try to match by:
        // 1. Email (Exact)
        // 2. Phone (Exact)
        // 3. Name (Exact match with displayName)
        // 4. Name (Contains email prefix - e.g. "ali" in "ali@test.com")
        const match = drivers.find(d => {
            const driverEmail = d.email?.toLowerCase();
            const driverName = d.name?.toLowerCase();

            // 1. Email check
            if (driverEmail && userEmail && driverEmail === userEmail) return true;

            // 2. Phone check
            if (d.phone && currentUser.phoneNumber && d.phone === currentUser.phoneNumber) return true;

            // 3. Display Name check
            if (userName && driverName && driverName === userName) return true;

            // 4. Email prefix check (fuzzy)
            // Only do this if we have a valid email prefix to check against
            if (userEmail) {
                const emailPrefix = userEmail.split('@')[0];
                if (emailPrefix.length > 2 && driverName?.includes(emailPrefix)) return true;
            }

            return false;
        });

        if (match) {
            console.log('Driver detected:', match.name);
        } else {
            console.log('No driver matched for current user.');
        }

        return match;
    }, [currentUser, drivers]);

    // Auto-select logged-in driver
    useEffect(() => {
        if (loggedInDriver) {
            setSelectedDriverId(loggedInDriver.id);
        }
    }, [loggedInDriver]);

    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        // Load drivers
        const driversQuery = query(collection(db, 'companies', companyId, 'drivers'), orderBy('name'));
        const unsubDrivers = onSnapshot(driversQuery, (snapshot) => {
            const loadedDrivers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setDrivers(loadedDrivers);
        });

        // Load subcontractors
        const subcontractorsQuery = query(collection(db, 'companies', companyId, 'subcontractors'));
        const unsubSubcontractors = onSnapshot(subcontractorsQuery, (snapshot) => {
            setSubcontractors(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'subcontractor' })));
        });

        // Load suppliers
        const suppliersQuery = query(collection(db, 'companies', companyId, 'suppliers'));
        const unsubSuppliers = onSnapshot(suppliersQuery, (snapshot) => {
            setSuppliers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'supplier' })));
        });

        // Load products
        const productsQuery = query(collection(db, 'companies', companyId, 'products'));
        const unsubProducts = onSnapshot(productsQuery, (snapshot) => {
            setProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        // Load transfers
        const transfersQuery = query(
            collection(db, 'companies', companyId, 'driverTransfers'),
            orderBy('date', 'desc')
        );
        const unsubTransfers = onSnapshot(transfersQuery, (snapshot) => {
            setTransfers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        return () => {
            unsubDrivers();
            unsubSubcontractors();
            unsubSuppliers();
            unsubProducts();
            unsubTransfers();
        };
    }, [userData, currentUser, selectedDriverId]);

    // Filtered transfers
    const filteredTransfers = useMemo(() => {
        return transfers.filter(transfer => {
            if (filterDriver !== 'all' && transfer.driverId !== filterDriver) return false;
            // Support both old 'subcontractorId' and generic 'locationId' or implied by context
            const locationId = transfer.locationId || transfer.subcontractorId;
            if (filterLocation !== 'all' && locationId !== filterLocation) return false;
            if (filterType !== 'all' && transfer.type !== filterType) return false;

            if (transfer.date && filterDateFrom) {
                const transferDate = new Date(transfer.date.seconds * 1000);
                const fromDate = new Date(filterDateFrom);
                if (transferDate < fromDate) return false;
            }

            if (transfer.date && filterDateTo) {
                const transferDate = new Date(transfer.date.seconds * 1000);
                const toDate = new Date(filterDateTo);
                toDate.setHours(23, 59, 59);
                if (transferDate > toDate) return false;
            }

            return true;
        });
    }, [transfers, filterDriver, filterLocation, filterType, filterDateFrom, filterDateTo]);

    // Filter products based on search term
    useEffect(() => {
        if (!searchTerm) {
            setFilteredProducts([]);
            return;
        }

        const term = searchTerm.toLowerCase();
        const results = products.filter(product => {
            const nameMatch = product.name?.toLowerCase().includes(term);
            const barcodeMatch = product.barcode?.toLowerCase() === term || product.barcode?.toLowerCase().includes(term);
            return nameMatch || barcodeMatch;
        });

        setFilteredProducts(results);

        // Exact barcode match auto-select
        const exactMatch = products.find(p => p.barcode?.toLowerCase() === term);
        if (exactMatch && results.length === 1) {
            handleProductSelect(exactMatch);
        }
    }, [searchTerm, products]);

    const handleAddProduct = () => {
        if (!currentProduct.productId || !currentProduct.quantity || Number(currentProduct.quantity) <= 0) {
            alert('Lütfen ürün seçin ve miktar girin.');
            return;
        }

        setItems(prev => [...prev, {
            ...currentProduct,
            id: Date.now(),
            quantity: Number(currentProduct.quantity)
        }]);

        setCurrentProduct({ productId: '', productName: '', quantity: '', unit: 'Adet' });
    };

    const handleRemoveProduct = (itemId) => {
        setItems(prev => prev.filter(item => item.id !== itemId));
    };

    const handleProductSelect = (product) => {
        setCurrentProduct(prev => ({
            ...prev,
            productId: product.id,
            productName: product.name,
            unit: product.unit || 'Adet'
        }));
        setSearchTerm('');
        setShowResults(false);
    };

    const handleSubmit = async () => {
        if (!selectedDriverId) {
            alert('Lütfen bir şoför seçin.');
            return;
        }
        if (!selectedLocationId) {
            alert('Lütfen bir fason/tedarikçi seçin.');
            return;
        }
        if (items.length === 0) {
            alert('Lütfen en az bir ürün ekleyin.');
            return;
        }

        setSubmitting(true);

        try {
            const companyId = userData.companyId;
            const driver = drivers.find(d => d.id === selectedDriverId);

            // Find selected location in either list
            const subcontractor = subcontractors.find(s => s.id === selectedLocationId);
            const supplier = suppliers.find(s => s.id === selectedLocationId);
            const selectedLocation = subcontractor || supplier;
            const locationType = subcontractor ? 'subcontractor' : 'supplier';

            const transferData = {
                driverId: selectedDriverId,
                driverName: driver?.name || '',
                // Save both legacy field for compatibility and new generic fields
                subcontractorId: selectedLocationId,
                subcontractorName: selectedLocation?.name || selectedLocation?.supplierName || '',
                locationId: selectedLocationId,
                locationName: selectedLocation?.name || selectedLocation?.supplierName || '',
                locationType: locationType,
                type: transferType,
                items: items.map(item => ({
                    productId: item.productId,
                    productName: item.productName,
                    quantity: item.quantity,
                    unit: item.unit
                })),
                note: note,
                date: serverTimestamp(),
                createdBy: currentUser?.uid || '',
                createdByEmail: currentUser?.email || ''
            };

            await addDoc(collection(db, 'companies', companyId, 'driverTransfers'), transferData);

            // Update subcontractor stock if tracking is enabled (which we assume it is now)
            if (locationType === 'subcontractor' && selectedLocationId) {
                const stockUpdatePromises = items.map(item => {
                    const stockRef = doc(db, 'companies', companyId, 'subcontractors', selectedLocationId, 'stocks', item.productId);

                    // Logic:
                    // Delivery (Bırak) -> Driver GIVES to Sub -> Sub Stock INCREASES (+)
                    // Pickup (Al) -> Driver TAKES from Sub -> Sub Stock DECREASES (-)
                    const quantityChange = transferType === 'delivery' ? item.quantity : -item.quantity;

                    return setDoc(stockRef, {
                        productId: item.productId,
                        productName: item.productName,
                        quantity: increment(quantityChange),
                        unit: item.unit,
                        lastUpdated: serverTimestamp()
                    }, { merge: true });
                });

                await Promise.all(stockUpdatePromises);
            }

            // Reset form
            if (!loggedInDriver) {
                setSelectedDriverId('');
            }
            setSelectedLocationId(''); // Reset location
            setTransferType('pickup');
            setItems([]);
            setNote('');

            alert('Transfer kaydı başarıyla oluşturuldu!');
        } catch (error) {
            console.error('Transfer kaydedilirken hata:', error);
            alert('Bir hata oluştu: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddDriver = async () => {
        if (!newDriverName.trim()) {
            alert('Şoför adı gerekli.');
            return;
        }

        try {
            const companyId = userData.companyId;
            await addDoc(collection(db, 'companies', companyId, 'drivers'), {
                name: newDriverName.trim(),
                phone: newDriverPhone.trim(),
                isActive: true,
                createdAt: serverTimestamp()
            });

            setNewDriverName('');
            setNewDriverPhone('');
            setShowAddDriver(false);
            alert('Şoför eklendi!');
        } catch (error) {
            console.error('Şoför eklenirken hata:', error);
            alert('Hata: ' + error.message);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <div style={{ textAlign: 'center', color: '#64748b' }}>
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
                    <div>Yükleniyor...</div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.headerTop}>
                    <div>
                        <h1 style={styles.title}>🚚 Şoför Hareketleri</h1>
                        <p style={styles.subtitle}>Fason ve Tedarikçilerden ürün alma/bırakma</p>
                    </div>
                    <div style={styles.headerActions}>
                        <button onClick={() => setShowAddDriver(true)} style={styles.buttonSecondary}>
                            ➕ Yeni Şoför
                        </button>
                        <Link to="/uretim/fason-tedarikciler" style={{ ...styles.buttonSecondary, textDecoration: 'none' }}>
                            🏭 Fasonlar
                        </Link>
                        <Link to="/tedarikciler" style={{ ...styles.buttonSecondary, textDecoration: 'none' }}>
                            📦 Tedarikçiler
                        </Link>
                    </div>
                </div>
            </div>

            {/* New Transfer Form */}
            <div style={styles.card}>
                <h3 style={styles.cardTitle}>📝 Yeni Hareket Kaydı</h3>

                {/* Transfer Type Toggle */}
                <div style={styles.typeToggle}>
                    <button
                        type="button"
                        style={{
                            ...styles.typeButton,
                            ...(transferType === 'pickup' ? styles.typeButtonPickup : {})
                        }}
                        onClick={() => setTransferType('pickup')}
                    >
                        <span style={styles.typeIcon}>📥</span>
                        <span style={styles.typeLabel}>Al (Giriş)</span>
                    </button>
                    <button
                        type="button"
                        style={{
                            ...styles.typeButton,
                            ...(transferType === 'delivery' ? styles.typeButtonDelivery : {})
                        }}
                        onClick={() => setTransferType('delivery')}
                    >
                        <span style={styles.typeIcon}>📤</span>
                        <span style={styles.typeLabel}>Bırak (Çıkış)</span>
                    </button>
                </div>

                <div style={styles.formGrid}>
                    {/* Driver Selection */}
                    <div style={styles.formGroup}>
                        <label style={styles.label}>🚗 Şoför</label>
                        {loggedInDriver ? (
                            <div style={{
                                ...styles.input,
                                background: '#f8fafc',
                                color: '#0f172a',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'default'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>🚗</span>
                                    <span>{loggedInDriver.name}</span>
                                </div>
                                <span style={{
                                    fontSize: '12px',
                                    background: '#dcfce7',
                                    color: '#166534',
                                    padding: '4px 8px',
                                    borderRadius: '12px',
                                    fontWeight: '600'
                                }}>
                                    Siz
                                </span>
                            </div>
                        ) : (
                            <select
                                style={styles.select}
                                value={selectedDriverId}
                                onChange={(e) => setSelectedDriverId(e.target.value)}
                            >
                                <option value="">Şoför Seçin</option>
                                {drivers.filter(d => d.isActive !== false).map(driver => (
                                    <option key={driver.id} value={driver.id}>{driver.name}</option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Location Selection (Subcontractor/Supplier) */}
                    <div style={styles.formGroup}>
                        <label style={styles.label}>🏭 Fason / Tedarikçi</label>
                        <select
                            style={styles.select}
                            value={selectedLocationId}
                            onChange={(e) => setSelectedLocationId(e.target.value)}
                        >
                            <option value="">Fason veya Tedarikçi Seçin</option>

                            {subcontractors.length > 0 && (
                                <optgroup label="Fasonlar">
                                    {subcontractors.filter(s => s.isActive !== false).map(sub => (
                                        <option key={sub.id} value={sub.id}>
                                            {sub.name || sub.supplierName}
                                        </option>
                                    ))}
                                </optgroup>
                            )}

                            {suppliers.length > 0 && (
                                <optgroup label="Tedarikçiler">
                                    {suppliers.map(sup => (
                                        <option key={sup.id} value={sup.id}>
                                            {sup.name}
                                        </option>
                                    ))}
                                </optgroup>
                            )}
                        </select>
                    </div>
                </div>

                {/* Product Selection */}
                {/* Product Selection */}
                <div style={{ ...styles.formGrid, alignItems: 'flex-end' }}>
                    <div style={{ ...styles.formGroup, position: 'relative' }}>
                        <label style={styles.label}>📦 Ürün Ara (İsim veya Barkod)</label>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <input
                                type="text"
                                style={{
                                    ...styles.input,
                                    paddingRight: '40px' // Space for icon
                                }}
                                placeholder="Barkod veya Ürün Adı Yazın..."
                                value={currentProduct.productId ? currentProduct.productName : searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setShowResults(true);
                                    if (currentProduct.productId) {
                                        setCurrentProduct(prev => ({ ...prev, productId: '', productName: '', unit: 'Adet' }));
                                    }
                                }}
                                onFocus={() => setShowResults(true)}
                                disabled={!!currentProduct.productId}
                            />
                            {!currentProduct.productId ? (
                                <button
                                    type="button"
                                    style={{
                                        position: 'absolute',
                                        right: '8px',
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '18px',
                                        cursor: 'pointer',
                                        color: '#64748b',
                                        padding: '4px'
                                    }}
                                    onClick={() => {
                                        setShowResults(true);
                                    }}
                                >
                                    🔍
                                </button>
                            ) : (
                                <button
                                    onClick={() => {
                                        setCurrentProduct({ productId: '', productName: '', quantity: '', unit: 'Adet' });
                                        setSearchTerm('');
                                    }}
                                    type="button"
                                    style={{
                                        position: 'absolute',
                                        right: '8px',
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '18px',
                                        cursor: 'pointer',
                                        color: '#ef4444',
                                        padding: '4px'
                                    }}
                                >
                                    ✕
                                </button>
                            )}
                        </div>

                        {showResults && searchTerm && !currentProduct.productId && (
                            <div style={styles.searchResults}>
                                {filteredProducts.length > 0 ? (
                                    filteredProducts.map(product => (
                                        <div
                                            key={product.id}
                                            style={styles.searchResultItem}
                                            onClick={() => handleProductSelect(product)}
                                        >
                                            <span>{product.name}</span>
                                            {product.barcode && (
                                                <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>
                                                    {product.barcode}
                                                </span>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ padding: '12px', color: '#64748b', textAlign: 'center' }}>
                                        Ürün bulunamadı
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Miktar</label>
                        <input
                            type="number"
                            style={styles.input}
                            placeholder="0"
                            value={currentProduct.quantity}
                            onChange={(e) => setCurrentProduct(prev => ({ ...prev, quantity: e.target.value }))}
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <button
                            type="button"
                            onClick={handleAddProduct}
                            style={{ ...styles.button, ...styles.buttonSuccess, width: '100%' }}
                        >
                            ➕ Ekle
                        </button>
                    </div>
                </div>

                {/* Added Items */}
                {items.length > 0 && (
                    <div style={{ marginTop: '16px' }}>
                        <label style={styles.label}>Eklenen Ürünler ({items.length})</label>
                        <ul style={styles.itemsList}>
                            {items.map(item => (
                                <li key={item.id} style={styles.itemRow}>
                                    <div style={styles.itemInfo}>
                                        <div style={styles.itemName}>{item.productName}</div>
                                        <div style={styles.itemQuantity}>{item.quantity} {item.unit}</div>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveProduct(item.id)}
                                        style={styles.removeButton}
                                    >
                                        ✕
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Note */}
                <div style={styles.formGroup}>
                    <label style={styles.label}>📝 Not (Opsiyonel)</label>
                    <input
                        type="text"
                        style={styles.input}
                        placeholder="Ek açıklama yazın..."
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />
                </div>

                {/* Submit Button */}
                <div style={styles.stickySubmit}>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        style={{ ...styles.button, width: '100%', opacity: submitting ? 0.7 : 1 }}
                    >
                        {submitting ? '⏳ Kaydediliyor...' : '✓ Hareketi Kaydet'}
                    </button>
                </div>
            </div>

            {/* Transfer History */}
            <div style={styles.card}>
                <h3 style={styles.cardTitle}>📋 Hareket Geçmişi</h3>

                {/* Filters */}
                <div style={styles.filterContainer}>
                    <select
                        style={styles.filterSelect}
                        value={filterDriver}
                        onChange={(e) => setFilterDriver(e.target.value)}
                    >
                        <option value="all">Tüm Şoförler</option>
                        {drivers.map(d => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                    </select>
                    <select
                        style={styles.filterSelect}
                        value={filterLocation}
                        onChange={(e) => setFilterLocation(e.target.value)}
                    >
                        <option value="all">Fason/Tedarikçi</option>

                        {subcontractors.length > 0 && (
                            <optgroup label="Fasonlar">
                                {subcontractors.map(s => (
                                    <option key={s.id} value={s.id}>{s.name || s.supplierName}</option>
                                ))}
                            </optgroup>
                        )}

                        {suppliers.length > 0 && (
                            <optgroup label="Tedarikçiler">
                                {suppliers.map(s => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </optgroup>
                        )}
                    </select>
                    <select
                        style={styles.filterSelect}
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                    >
                        <option value="all">Tüm İşlemler</option>
                        <option value="pickup">Alma</option>
                        <option value="delivery">Bırakma</option>
                    </select>
                    <input
                        type="date"
                        style={styles.filterInput}
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                    />
                    <input
                        type="date"
                        style={styles.filterInput}
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                    />
                </div>

                {/* Table */}
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Tarih</th>
                                <th style={styles.th}>Tip</th>
                                <th style={styles.th}>Şoför</th>
                                <th style={styles.th}>Fason/Tedarikçi</th>
                                <th style={styles.th}>Ürünler</th>
                                <th style={styles.th}>Not</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTransfers.length === 0 ? (
                                <tr>
                                    <td colSpan={6} style={{ ...styles.td, textAlign: 'center', padding: '40px' }}>
                                        <div style={styles.emptyIcon}>📭</div>
                                        <div style={{ color: '#64748b' }}>Henüz hareket kaydı yok</div>
                                    </td>
                                </tr>
                            ) : (
                                filteredTransfers.map(transfer => (
                                    <tr key={transfer.id}>
                                        <td style={styles.td}>
                                            {transfer.date
                                                ? new Date(transfer.date.seconds * 1000).toLocaleString('tr-TR')
                                                : '-'}
                                        </td>
                                        <td style={styles.td}>
                                            <span style={{
                                                ...styles.badge,
                                                ...(transfer.type === 'pickup' ? styles.badgePickup : styles.badgeDelivery)
                                            }}>
                                                {transfer.type === 'pickup' ? 'Alış' : 'Teslimat'}
                                            </span>
                                        </td>
                                        <td style={styles.td}>{transfer.driverName}</td>
                                        <td style={styles.td}>
                                            <div style={{ fontWeight: 500 }}>{transfer.locationName || transfer.subcontractorName}</div>
                                            {transfer.locationType === 'supplier' && (
                                                <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                                                    Tedarikçi
                                                </span>
                                            )}
                                            {transfer.locationType === 'subcontractor' && (
                                                <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                                                    Fason
                                                </span>
                                            )}
                                        </td>
                                        <td style={styles.td}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                {transfer.items?.map((item, idx) => (
                                                    <div key={idx} style={{ fontSize: '13px' }}>
                                                        {item.productName}: <strong>{item.quantity}</strong> {item.unit}
                                                    </div>
                                                ))}
                                            </div>
                                        </td>
                                        <td style={styles.td}>{transfer.note || '-'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add Driver Modal */}
            {showAddDriver && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '24px',
                        width: '100%',
                        maxWidth: '400px'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>➕ Yeni Şoför Ekle</h3>
                            <button
                                onClick={() => setShowAddDriver(false)}
                                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b' }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Şoför Adı *</label>
                            <input
                                type="text"
                                style={styles.input}
                                placeholder="Ad Soyad"
                                value={newDriverName}
                                onChange={(e) => setNewDriverName(e.target.value)}
                            />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Telefon</label>
                            <input
                                type="tel"
                                style={styles.input}
                                placeholder="05XX XXX XX XX"
                                value={newDriverPhone}
                                onChange={(e) => setNewDriverPhone(e.target.value)}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                            <button
                                onClick={() => setShowAddDriver(false)}
                                style={{ ...styles.buttonSecondary, flex: 1 }}
                            >
                                İptal
                            </button>
                            <button
                                onClick={handleAddDriver}
                                style={{ ...styles.button, ...styles.buttonSuccess, flex: 1 }}
                            >
                                ✓ Kaydet
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>

    );
}

export default DriverLogistics;
